import React from 'react';
import {
  ShoppingCart,
  Plus,
  ArrowLeft,
  Search,
  X,
  Eye,
  EyeOff,
  Scale
} from 'lucide-react';

const PDVProductsPanel = ({
  onClose,
  openWeightModal,
  showProductImages,
  setShowProductImages,
  step,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  products,
  categoriesWithCount,
  filteredProducts,
  addingToCart,
  cart,
  addToCart
}) => {
  return (
        <div className={`flex flex-col h-full min-h-0 lg:basis-[60%] lg:grow-0 lg:shrink-0 lg:min-w-0 lg:max-w-[60%] overflow-hidden ${step === 'checkout' ? 'hidden lg:flex' : 'flex flex-1 lg:flex-none'}`}>
          
          {/* Desktop Header */}
          <div className="hidden lg:block bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 shadow-xl px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl flex items-center justify-center transition-all"
                  data-testid="close-pdv-button-desktop"
                >
                  <ArrowLeft className="w-6 h-6 text-white" />
                </button>
                <div>
                  <h1 className="text-white font-bold text-2xl tracking-tight">Ponto de Venda</h1>
                  <p className="text-orange-100 text-sm font-medium">Selecione os produtos e finalize o pedido</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={openWeightModal}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl px-4 py-2.5 transition-all flex items-center gap-2"
                  data-testid="weight-button-desktop"
                >
                  <Scale className="w-5 h-5 text-white" />
                  <span className="text-white text-sm font-medium">Peso</span>
                </button>
                <button
                  onClick={() => setShowProductImages(!showProductImages)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl px-4 py-2.5 transition-all flex items-center gap-2"
                  data-testid="toggle-images-button-desktop"
                >
                  {showProductImages ? (
                    <>
                      <Eye className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-medium">Ocultar Imagens</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-medium">Mostrar Imagens</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {step === 'menu' ? (
            <div className="flex-1 flex flex-col h-full bg-gradient-to-br from-slate-50 to-white">
              {/* Search Bar */}
              <div className="p-4 lg:p-6 bg-white border-b border-slate-200 flex-shrink-0 z-10">
                <div className="relative max-w-2xl">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar produtos por nome ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm lg:text-base"
                    data-testid="search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="px-4 lg:px-6 py-3 bg-white border-b border-slate-200 flex-shrink-0 min-w-0 overflow-hidden">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide min-w-0">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-5 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all flex-shrink-0 ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="category-all"
                  >
                    Todos ({products.length})
                  </button>
                  {categoriesWithCount.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-5 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all flex-shrink-0 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Products Grid */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 pdv-scroll" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', maxHeight: '100%' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 max-w-7xl pb-8">
                  {filteredProducts.map((product) => {
                    const isAdding = addingToCart === product.id;
                    const inCart = cart.find(item => item.id === product.id);
                    
                    return (
                      <div
                        key={product.id}
                        className={`bg-white rounded-2xl p-4 border-2 border-slate-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300 group ${isAdding ? 'scale-95 border-orange-500' : ''}`}
                        data-testid={`product-${product.id}`}
                      >
                        {/* Image */}
                        {showProductImages && (
                          <div className="relative aspect-square bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl mb-3 overflow-hidden group-hover:shadow-lg transition-shadow">
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-30" style={{ display: product.image_url ? 'none' : 'flex' }}>
                              🍽️
                            </div>
                            
                            {/* Badge if in cart */}
                            {inCart && (
                              <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow-lg flex items-center gap-1">
                                <ShoppingCart className="w-3 h-3" />
                                {inCart.quantity}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors">
                            {product.name}
                          </h3>
                          {product.description && (
                            <p className="text-slate-500 text-xs lg:text-sm line-clamp-2 mb-3">
                              {product.description}
                            </p>
                          )}
                        </div>

                        {/* Price and Add Button */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                          <span className="text-orange-600 font-bold text-xl lg:text-2xl">
                            R$ {product.price.toFixed(2)}
                          </span>
                          <button
                            onClick={() => addToCart(product)}
                            disabled={isAdding}
                            className={`bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl px-4 py-2.5 font-bold text-sm transition-all flex items-center gap-2 shadow-lg hover:shadow-xl active:scale-95 ${isAdding ? 'opacity-50' : ''}`}
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Search className="w-20 h-20 mb-4 opacity-20" />
                    <p className="text-xl font-medium text-slate-500">Nenhum produto encontrado</p>
                    <p className="text-sm text-slate-400 mt-2">Tente buscar com outros termos</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
  );
};

export default PDVProductsPanel;
